// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ApplyInfoRow from './ApplyInfoRow';
import TableLoader from "../../../../dashboard/common/TableLoader";

const AllRequestedWorkView = ({
  contractor,
  isShowAllWork,
  selectedApplies,
  selectApplies,
}) => {
  const [applyInfoArray, setApplyInfoArray] = useState([]);
  const [currentApply, setCurrentApply] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const getApplyInfo = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `/api/client/contractors/${contractor.contractor_id}/apply/all`,
      );

      const applies = data?.payload?.applies || [];
      const otherApplies = [];

      applies.forEach((apply) => {
        if (apply.workOrderId === contractor.work_order_id) {
          setCurrentApply(apply);
        } else {
          otherApplies.push(apply);
        }
      });

      setApplyInfoArray(otherApplies);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [contractor]);

  useEffect(() => {
    if (isShowAllWork) {
      getApplyInfo();
    }
  }, [isShowAllWork, getApplyInfo]);

  const getNewApplyList = (target) => {
    if (!currentApply) return selectedApplies;

    const currentId = currentApply.workOrderId;

    if (target.checked) {
      if (target.name === 'selectAll') {
        return [
          currentId,
          ...applyInfoArray.map((a) => a.workOrderId),
        ];
      }
      return [currentId, ...selectedApplies, target.name];
    }

    if (target.name === 'selectAll') {
      return [currentId];
    }

    const newSelected = new Set(selectedApplies);
    newSelected.delete(target.name);

    return [currentId, ...newSelected];
  };

  const handlerInputChanges = (event) => {
    selectApplies([...new Set(getNewApplyList(event.target))]);
  };

  if (!isShowAllWork) return null;

  const isSelectAllChecked =
    currentApply
    && selectedApplies.length === applyInfoArray.length + 1;

  return (
    <tr>
      <td colSpan="3" className="no-paddings">
        {isLoading ? (
          <TableLoader />) :
          !currentApply && applyInfoArray.length === 0 ? (
            <div className="empty-text">
              This contractor has no other requested work
            </div>
          ) : (
            <table className="apply-table">
              <colgroup>
                <col style={{ width: '100px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '240px' }} />
                <col style={{ width: '170px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '200px' }} />
                <col style={{ width: '180px' }} />
              </colgroup>

              <tbody>
                {/* HEADER */}
                <tr className="apply-table-header">
                  <td>WIN</td>
                  <td>WO ID</td>
                  <td>WO TITLE</td>
                  <td>TRAVEL EXPENSE</td>
                  <td>START DATE</td>
                  <td>ADDRESS</td>
                  <td>PRICE</td>
                </tr>

                {/* CURRENT APPLY */}
                {currentApply && (
                  <ApplyInfoRow
                    applyInfo={currentApply}
                    current
                    selectedApplies={selectedApplies}
                    handlerInputChanges={() => { }}
                  />
                )}

                {/* SELECT ALL */}
                {applyInfoArray.length > 0 && (
                  <tr>
                    <td className="table-cell">
                      <label className="checkbox-wrapper check_btn" style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          name="selectAll"
                          checked={isSelectAllChecked}
                          onChange={handlerInputChanges}
                        />
                        <span className="checkbox-label">Select All</span>
                      </label>
                    </td>
                  </tr>
                )}

                {/* OTHER APPLIES */}
                {applyInfoArray.map((applyInfo) => (
                  <ApplyInfoRow
                    key={applyInfo.workOrderId}
                    applyInfo={applyInfo}
                    selectedApplies={selectedApplies}
                    handlerInputChanges={handlerInputChanges}
                  />
                ))}
              </tbody>
            </table>
          )}
      </td>
    </tr>
  );
};

export default AllRequestedWorkView;